'use client'

import React from 'react'

import type { TShipmentMember, TShipmentTrackingReturnType } from '../../types/shipment'
import type {
  TAPISource,
  TELDAppSource,
  TExternalTrackingLinkSource,
  TMobileAppTrackingSource,
  TSFTPSource,
  TShipmentTrackingSourceTypes,
  TTiveDeviceSource,
  TTMSAppSource,
  TVisibilityAppSource
} from '../../types/tracking'
import { ShipmentTrackingSourceEnum } from '../../types/tracking'
import { APISource } from './api-source'
import { ELDAppSource } from './eld-app-source'
import { ExternalTrackingLinkSource } from './external-tracking-source'
import { MobileAppTrackingSource } from './mobile-app-tracking-source'
import { SFTPSource } from './sftp-source'
import { TiveDeviceSource } from './tive-device-source'
import { TMSAppSource } from './tms-app'
import { VisibilityAppSource } from './visibility-app-source'

interface TrackingSourcesProps {
  drivers?: TShipmentMember[]
  shipmentID: string
  shipmentNumber: string
  shipmentOwnerWorkspaceID?: string
  shipmentTracking: TShipmentTrackingReturnType
}

/**
 * This component is used to render the tracking source information.
 */
const TrackingSourceComponent: React.FC<{
  drivers?: TShipmentMember[]
  index: number
  shipmentID: string
  shipmentNumber: string
  shipmentOwnerWorkspaceID?: string
  trackingSource: TShipmentTrackingSourceTypes
}> = ({ trackingSource, index, shipmentID, shipmentNumber, drivers, shipmentOwnerWorkspaceID }) => {
  switch (trackingSource.source) {
    case ShipmentTrackingSourceEnum.API:
      return <APISource trackingSourceData={trackingSource as TAPISource} key={index} shipmentID={shipmentID} />

    case ShipmentTrackingSourceEnum.MobileApp:
      return (
        <MobileAppTrackingSource
          key={index}
          trackingSourceData={trackingSource as TMobileAppTrackingSource}
          shipmentID={shipmentID}
          shipmentNumber={shipmentNumber}
          drivers={drivers}
          shipmentOwnerWorkspaceID={shipmentOwnerWorkspaceID}
        />
      )

    case ShipmentTrackingSourceEnum.CSVFile:
      return <SFTPSource trackingSourceData={trackingSource as TSFTPSource} key={index} shipmentID={shipmentID} />

    case ShipmentTrackingSourceEnum.EDIFile:
      return <SFTPSource trackingSourceData={trackingSource as TSFTPSource} key={index} shipmentID={shipmentID} />

    case ShipmentTrackingSourceEnum.TiveDevice:
      return (
        <TiveDeviceSource
          trackingSourceData={trackingSource as TTiveDeviceSource}
          key={index}
          shipmentID={shipmentID}
        />
      )

    case ShipmentTrackingSourceEnum.ELD:
      return <ELDAppSource trackingSourceData={trackingSource as TELDAppSource} key={index} />

    case ShipmentTrackingSourceEnum.ExternalTrackingLink:
      return (
        <ExternalTrackingLinkSource
          trackingSourceData={trackingSource as TExternalTrackingLinkSource}
          key={index}
          shipmentID={shipmentID}
        />
      )

    case ShipmentTrackingSourceEnum.TMSApp:
      return <TMSAppSource trackingSourceData={trackingSource as TTMSAppSource} key={index} shipmentID={shipmentID} />

    case ShipmentTrackingSourceEnum.VisibilityProvider:
      return (
        <VisibilityAppSource
          trackingSourceData={trackingSource as TVisibilityAppSource}
          key={index}
          shipmentID={shipmentID}
        />
      )

    default:
      return null
  }
}

/**
 * Renders the tracking sources and relevant information for each source
 */
export const TrackingSources = (props: TrackingSourcesProps) => {
  const { shipmentTracking, shipmentID, shipmentNumber, drivers, shipmentOwnerWorkspaceID } = props

  return (
    <div className="flex w-full flex-col gap-2 items-start">
      {shipmentTracking.data?.map((trackingSource, index) => (
        <div key={index} className="w-full rounded-md border border-border bg-card/40 p-2">
          <TrackingSourceComponent
            trackingSource={trackingSource}
            index={index}
            shipmentID={shipmentID}
            drivers={drivers}
            shipmentNumber={shipmentNumber}
            shipmentOwnerWorkspaceID={shipmentOwnerWorkspaceID}
          />
        </div>
      ))}
    </div>
  )
}
